import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getSupabaseClient } from '../supabase';
import { useAuth } from '../hooks/useAuth';
import {
  DEV_MODE,
  FeatureName,
  BASIC_FEATURES,
  DEV_MODE_FEATURES,
  PLAN_FEATURES,
  normalizePlanType,
} from '../config/features.config';
import {
  applyMaturityFilter,
  MODULE_CATALOG,
  nonStableModuleKeys,
  resolveNonStableKeys,
  type ModuleStatus,
} from '../config/modules.catalog';

interface FeatureFlags {
  [key: string]: boolean;
}

export interface FeatureFlagsContextValue {
  features: FeatureFlags;
  loading: boolean;
  isFeatureEnabled: (featureName: FeatureName) => boolean;
  getAllEnabledFeatures: () => FeatureName[];
  /** Tras mutar `tenant_features` o plan: refresca flags sin recargar el documento (SPA). */
  refetchFeatures: () => Promise<void>;
  isDevMode: boolean;
  /** Cuenta interna (tenants.is_internal): ve también los módulos en desarrollo/beta. */
  isInternal: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

/**
 * Estado global de feature flags (una sola fuente de verdad para sidebar, rutas protegidas y ajustes).
 * Debe envolver la app donde exista sesión / router.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { tenantId, loading: authLoading } = useAuth();
  const [features, setFeatures] = useState<FeatureFlags>({});
  const [loading, setLoading] = useState(true);
  const [isInternal, setIsInternal] = useState(false);

  const loadFeatures = useCallback(async () => {
    const supabase = getSupabaseClient();
    try {
      if (DEV_MODE) {
        if (import.meta.env?.DEV) {
          console.warn('🔧 [FeatureFlags] Modo desarrollo: TODOS los módulos habilitados');
        }
        const allFeatures: FeatureFlags = {};
        DEV_MODE_FEATURES.forEach((feature) => {
          allFeatures[feature] = true;
        });
        setIsInternal(true); // en dev se ve todo, incl. módulos en desarrollo
        setFeatures(allFeatures);
        setLoading(false);
        return;
      }

      if (!tenantId) {
        setLoading(false);
        return;
      }

      // tenants: maybeSingle evita error duro si RLS devuelve 0 filas; no tirar todo el árbol de flags.
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('plan_type, is_internal')
        .eq('id', tenantId)
        .maybeSingle();

      if (tenantError) {
        console.warn('⚠️ [FeatureFlags] No se pudo leer tenants.plan_type; usando basic + tenant_features', {
          tenantId,
          error: tenantError,
        });
      }

      const planType = normalizePlanType(tenantData?.plan_type as string | undefined);
      // Cuenta interna (Sandbox/Dulti/Vitalex): ve también módulos en desarrollo/beta.
      const tenantIsInternal = tenantData?.is_internal === true;
      setIsInternal(tenantIsInternal);
      const { data: tenantFeatures, error: featuresError } = await supabase
        .from('tenant_features')
        .select('feature_name, is_enabled')
        .eq('tenant_id', tenantId);

      if (featuresError) {
        console.warn('⚠️ [FeatureFlags] No se pudo leer tenant_features; solo baseline por plan', {
          tenantId,
          planType,
          error: featuresError,
        });
      }

      // Baseline del plan. Fuente EDITABLE = plans.features (BD); fallback de fábrica =
      // PLAN_FEATURES (código). La tabla se sembró con los mismos valores, así que leer
      // BD es un no-op funcional. Si plans no responde o trae claves inválidas, caemos
      // al código: el gating nunca se queda sin baseline. Filtramos a módulos reales del
      // catálogo por defensa ante data vieja.
      let planFeatures: FeatureName[] = PLAN_FEATURES[planType] ?? PLAN_FEATURES.basic;
      const { data: planRow, error: planError } = await supabase
        .from('plans')
        .select('features')
        .eq('slug', planType)
        .eq('is_active', true)
        .maybeSingle();
      if (planError) {
        console.warn('⚠️ [FeatureFlags] No se pudo leer plans; usando PLAN_FEATURES (código)', {
          planType,
          error: planError,
        });
      } else if (Array.isArray(planRow?.features)) {
        const valid = (planRow.features as unknown[]).filter(
          (f): f is FeatureName => typeof f === 'string' && f in MODULE_CATALOG,
        );
        if (valid.length) planFeatures = valid;
      }

      const featuresMap: FeatureFlags = {};
      [...BASIC_FEATURES, ...planFeatures].forEach((feature) => {
        featuresMap[feature] = true;
      });

      if (!featuresError && tenantFeatures?.length) {
        tenantFeatures.forEach((feature) => {
          featuresMap[feature.feature_name] = feature.is_enabled;
        });
      }

      // Kill switches operacionales: overrides SIEMPRE ganan (apagar módulos en runtime sin deploy)
      const { data: overrides, error: overridesError } = await supabase
        .from('tenant_feature_overrides')
        .select('feature_name, is_enabled')
        .eq('tenant_id', tenantId);

      if (!overridesError && overrides?.length) {
        overrides.forEach((ov) => {
          featuresMap[ov.feature_name] = ov.is_enabled;
        });
      }

      // Status EFECTIVO de módulos: override de BD (platform_module_status, editable
      // por el super admin) con fallback al catálogo (código). Así pasar un módulo a
      // "estable" tiene efecto sin deploy. Si la tabla no responde, usamos el catálogo.
      let nonStable = nonStableModuleKeys();
      const { data: statusRows, error: statusError } = await supabase
        .from('platform_module_status')
        .select('module_key, status');
      if (!statusError && Array.isArray(statusRows) && statusRows.length) {
        const overrides: Record<string, ModuleStatus> = {};
        statusRows.forEach((row) => {
          overrides[row.module_key as string] = row.status as ModuleStatus;
        });
        nonStable = resolveNonStableKeys(overrides);
      }

      // Filtro de MADUREZ (última capa, gana para OCULTAR): un módulo en desarrollo/beta
      // se apaga para cuentas de cliente aunque el plan o un override lo encienda; las
      // cuentas internas (is_internal) lo siguen viendo. Ver applyMaturityFilter.
      setFeatures(applyMaturityFilter(featuresMap, tenantIsInternal, nonStable));
      if (import.meta.env?.DEV) {
        console.warn('🔒 [FeatureFlags] Modo producción: Features según plan');
      }
    } catch (error) {
      console.error('❌ Error al cargar features:', error);
      // Último recurso: solo tenant_features (sin plan), para no dejar ecommerce en false por un fallo puntual.
      try {
        if (tenantId) {
          const { data: rows, error: tfErr } = await supabase
            .from('tenant_features')
            .select('feature_name, is_enabled')
            .eq('tenant_id', tenantId);
          if (!tfErr && rows?.length) {
            const map: FeatureFlags = {};
            BASIC_FEATURES.forEach((f) => {
              map[f] = true;
            });
            rows.forEach((r) => {
              map[r.feature_name] = r.is_enabled;
            });
            setFeatures(map);
            return;
          }
        }
      } catch {
        /* ignore */
      }
      const fallbackFeatures: FeatureFlags = {};
      BASIC_FEATURES.forEach((feature) => {
        fallbackFeatures[feature] = true;
      });
      setFeatures(fallbackFeatures);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (DEV_MODE) {
      void loadFeatures();
      return;
    }
    if (!tenantId) {
      setLoading(true);
      return;
    }
    void loadFeatures();
  }, [tenantId, authLoading, loadFeatures]);

  const isFeatureEnabled = useCallback(
    (featureName: FeatureName): boolean => features[featureName] ?? false,
    [features]
  );

  const getAllEnabledFeatures = useCallback((): FeatureName[] => {
    return Object.keys(features).filter((feature) => features[feature]) as FeatureName[];
  }, [features]);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      features,
      loading,
      isFeatureEnabled,
      getAllEnabledFeatures,
      refetchFeatures: loadFeatures,
      isDevMode: DEV_MODE,
      isInternal,
    }),
    [features, loading, isFeatureEnabled, getAllEnabledFeatures, loadFeatures, isInternal]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlags debe usarse dentro de <FeatureFlagsProvider>.');
  }
  return ctx;
}
